import { useMemo } from 'react';

import { IFeatureMenuQuery, useFeatureMenuQuery } from 'generated/sanity-graphql';
import { useFeaturesContext } from 'state/features';
import { useServiceModeContext } from 'state/service-mode';
import { isCatering } from 'utils/service-mode';

import { FeatureMenuPage } from './types';
import { useFeatureMenuPageComponents } from './use-feature-menu-page-components';

interface IUseFeatureMenuValue {
  menuId: string;
  featureMenu: IFeatureMenuQuery['FeatureMenu'];
  featureMenuLoading: boolean;
  featureMenuPageComponents: FeatureMenuPage;
}

export const useFeatureMenuPage = (): IUseFeatureMenuValue => {
  const { featureMenuId = '', featureIdsLoading } = useFeaturesContext();
  const { serviceMode } = useServiceModeContext();

  const { data, loading: featureMenuPageComponentIdsLoading } = useFeatureMenuQuery({
    variables: { featureMenuId },
    skip: !useFeatureMenuPage,
  });

  const featureMenuPage = data?.FeatureMenu;

  const {
    featureMenuPageComponents,
    featureMenuPageComponentsLoading,
  } = useFeatureMenuPageComponents(featureMenuPage ?? undefined);

  const featureMenuPageWithPopulatedComponents = useMemo(() => {
    if (!featureMenuPage || !featureMenuPageComponents) {
      return null;
    }
    if (featureMenuPageComponents) {
      return { ...featureMenuPage, components: featureMenuPageComponents };
    }
    return featureMenuPage;
  }, [featureMenuPage, featureMenuPageComponents]);

  const featureMenuLoading =
    featureIdsLoading || featureMenuPageComponentIdsLoading || featureMenuPageComponentsLoading;

  const featureMenu = data?.FeatureMenu ?? null;
  const { cateringMenu, defaultMenu } = featureMenu || {};

  const menuId: string = (isCatering(serviceMode) ? cateringMenu : defaultMenu)?._id || '';

  return {
    featureMenuLoading,
    featureMenu,
    featureMenuPageComponents: featureMenuPageWithPopulatedComponents as FeatureMenuPage,
    menuId,
  };
};
